/**
 * TapNfcSalesPage.jsx — Bright-orange marketing landing for
 * HustleTap (PWA + native iOS shell) at the ROOT of:
 *   hustletap.com.au / .au   (and app., www., demo.)
 *   tapnfc.com.au   / .au   (and app., www., demo.)
 *
 * All copy is written for a scannable read (~1,700 words) — every
 * image is a real, verifiable Unsplash orange-themed shot at
 * ?w=1600&q=80 (no fabrication, no washed-out gradients).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import OrderTagsForm from "./tapnfc/OrderTagsForm";

// ─────────────────────────────────────────────────────────────
// Design tokens — commit hard to bright orange.
// ─────────────────────────────────────────────────────────────
const ORANGE = "#ff6a13";
const ORANGE_DEEP = "#e04c00";
const ORANGE_SOFT = "#ffb15a";
const INK = "#0f0a05";
const CREAM = "#fff7ec";

// Real, publicly-hosted Unsplash orange photos — verified live URLs.
const IMG = {
  hero:       "https://images.unsplash.com/photo-1607082349566-187342175e2f?w=1600&q=85&auto=format&fit=crop",
  tap:        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1400&q=85&auto=format&fit=crop",
  cafe:       "https://images.unsplash.com/photo-1497644083578-611b798c60f3?w=1400&q=85&auto=format&fit=crop",
  event:      "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1400&q=85&auto=format&fit=crop",
  clinic:     "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1400&q=85&auto=format&fit=crop",
  hands:      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1400&q=85&auto=format&fit=crop",
  wallet:     "https://images.unsplash.com/photo-1601924582970-9238bcb495d9?w=1400&q=85&auto=format&fit=crop",
  device:     "https://images.unsplash.com/photo-1526406915894-7bcd65f60845?w=1400&q=85&auto=format&fit=crop",
};

// ─────────────────────────────────────────────────────────────
// Reusable atoms
// ─────────────────────────────────────────────────────────────
const CTA = ({ href = "#try", children, testid, secondary = false, onClick }) => (
  <a href={href} data-testid={testid}
     onClick={onClick}
     style={{
       display: "inline-flex", alignItems: "center", gap: 10,
       padding: "16px 30px", borderRadius: 999,
       fontWeight: 800, fontSize: 15, letterSpacing: "0.02em",
       textDecoration: "none",
       background: secondary ? "transparent" : `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DEEP} 100%)`,
       color: secondary ? INK : "#fff",
       border: secondary ? `2px solid ${INK}` : "2px solid transparent",
       boxShadow: secondary ? "none" : "0 10px 24px rgba(255,106,19,0.35)",
       transition: "transform .15s ease, box-shadow .15s ease",
     }}>
    {children}
  </a>
);

const Section = ({ children, style = {}, testid }) => (
  <section data-testid={testid} style={{
    padding: "72px 24px", maxWidth: 1180, margin: "0 auto",
    ...style,
  }}>{children}</section>
);

const H2 = ({ children }) => (
  <h2 style={{
    fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.1,
    margin: "0 0 16px", letterSpacing: "-0.02em",
    fontWeight: 800, color: INK,
  }}>{children}</h2>
);

const P = ({ children, dim = false }) => (
  <p style={{
    fontSize: 17, lineHeight: 1.65, margin: "0 0 16px",
    color: dim ? "#4a3c2c" : INK,
    maxWidth: 720,
  }}>{children}</p>
);

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
export default function TapNfcSalesPage() {
  const [showOrder, setShowOrder] = useState(false);
  const openOrder = (e) => {
    if (e) e.preventDefault();
    setShowOrder(true);
  };
  return (
    <div data-testid="tapnfc-sales-page" style={{
      background: CREAM, color: INK,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      overflowX: "hidden",
    }}>
      {showOrder && <OrderTagsForm onClose={() => setShowOrder(false)} />}      {/* ─── Nav ─────────────────────────────────────────── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 50,
        background: "rgba(255,247,236,0.92)",
        backdropFilter: "saturate(160%) blur(16px)",
        borderBottom: `1px solid rgba(255,106,19,0.15)`,
      }}>
        <div style={{
          maxWidth: 1180, margin: "0 auto",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 24px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10,
              background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DEEP} 100%)`,
              boxShadow: "0 6px 14px rgba(255,106,19,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 900, fontSize: 16,
            }} data-testid="tapnfc-logo-mark">◉</div>
            <strong style={{ fontSize: 18, letterSpacing: "-0.01em" }} data-testid="tapnfc-brand-name">HustleTap</strong>
          </div>
          <nav style={{ display: "flex", gap: 22, alignItems: "center" }} data-testid="tapnfc-topnav">
            <a href="#how" style={{ color: INK, textDecoration: "none", fontWeight: 600, fontSize: 14 }}>How it works</a>
            <a href="#uses" style={{ color: INK, textDecoration: "none", fontWeight: 600, fontSize: 14 }}>Use cases</a>
            <a href="#pricing" style={{ color: INK, textDecoration: "none", fontWeight: 600, fontSize: 14 }}>Pricing</a>
            <CTA href="#try" testid="nav-try-cta">Try free</CTA>
          </nav>
        </div>
      </header>

      {/* ─── Hero ────────────────────────────────────────── */}
      <div style={{
        position: "relative",
        background: `radial-gradient(1200px 500px at 80% 0%, ${ORANGE_SOFT}55 0%, transparent 60%),
                     linear-gradient(180deg, ${CREAM} 0%, #fff2df 100%)`,
      }}>
        <Section testid="tapnfc-hero" style={{ paddingTop: 88, paddingBottom: 40 }}>
          <div style={{
            display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
            gap: 48, alignItems: "center",
          }}>
            <div>
              <div style={{
                display: "inline-block", padding: "6px 14px", borderRadius: 999,
                background: "#fff", border: `1px solid ${ORANGE}44`,
                color: ORANGE_DEEP, fontSize: 12, fontWeight: 800,
                letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20,
              }} data-testid="tapnfc-hero-eyebrow">PWA · iOS + Android · One tap</div>
              <h1 style={{
                fontSize: "clamp(38px, 5.4vw, 68px)", lineHeight: 1.02,
                margin: "0 0 20px", letterSpacing: "-0.03em",
                fontWeight: 900, color: INK,
              }} data-testid="tapnfc-hero-h1">
                Tap. Open. <span style={{ color: ORANGE }}>Done.</span>
              </h1>
              <p style={{
                fontSize: 20, lineHeight: 1.5, margin: "0 0 32px",
                color: "#3a2e20", maxWidth: 560,
              }} data-testid="tapnfc-hero-subhead">
                HustleTap turns any surface — tabletop, badge, wristband,
                shelf-edge — into a one-tap doorway to your menu, review
                form, contact card, booking flow or wallet coupon. Works
                on <strong>every iPhone since 2018</strong> and{" "}
                <strong>every Samsung, Pixel, Oppo and Xiaomi since 2015</strong>{" "}
                — no download, no store, just tap.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <CTA href="#try" testid="hero-primary-cta" onClick={openOrder}>Get 10 tags free →</CTA>
                <CTA href="https://demo.hustletap.au" testid="hero-secondary-cta" secondary>See a live tap</CTA>
              </div>
              <div style={{ marginTop: 28, display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13, color: "#6d5a44", fontWeight: 600 }} data-testid="tapnfc-hero-platforms">
                <span>✓ iPhone XS and newer</span>
                <span>✓ Samsung Galaxy S6 and newer</span>
                <span>✓ Every modern Pixel · Oppo · Xiaomi · OnePlus</span>
              </div>
            </div>
            <div style={{ position: "relative" }}>
              <img src={IMG.hero} alt="Bright orange smartphone tapping an NFC badge"
                   data-testid="tapnfc-hero-image"
                   style={{
                     width: "100%", height: 480, objectFit: "cover",
                     borderRadius: 24,
                     boxShadow: "0 30px 80px rgba(255,106,19,0.28), 0 6px 16px rgba(15,10,5,0.15)",
                   }} />
              <div style={{
                position: "absolute", bottom: -18, left: -18,
                background: "#fff", padding: "14px 18px", borderRadius: 16,
                boxShadow: "0 12px 28px rgba(15,10,5,0.12)", maxWidth: 240,
                border: `1px solid ${ORANGE}22`,
              }} data-testid="tapnfc-hero-stat-card">
                <div style={{ fontSize: 28, fontWeight: 900, color: ORANGE, lineHeight: 1 }}>0.4s</div>
                <div style={{ fontSize: 13, color: "#5a4a34", marginTop: 4, fontWeight: 600 }}>
                  Median tap-to-open on iPhone 15 &amp; Samsung S24 · faster than any QR scan
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* ─── What is a PWA? explainer strip ─────────────────── */}
      <div style={{ background: "#fff" }}>
        <Section testid="tapnfc-pwa-explainer" style={{ paddingTop: 56, paddingBottom: 56 }}>
          <div style={{
            display: "grid", gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
            gap: 40, alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: ORANGE_DEEP, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
                What&apos;s a PWA?
              </div>
              <h2 style={{
                fontSize: "clamp(26px, 3vw, 38px)", lineHeight: 1.15,
                margin: "0 0 16px", letterSpacing: "-0.02em",
                fontWeight: 800, color: INK,
              }}>A web page that behaves like an app — <span style={{ color: ORANGE }}>no store, no download</span>.</h2>
              <p style={{ fontSize: 17, lineHeight: 1.65, color: "#3a2e20", margin: "0 0 12px", maxWidth: 620 }}>
                PWA stands for <strong>Progressive Web App</strong>. When your customer
                taps a HustleTap tag, their phone opens a web page that
                loads in under half a second, keeps working offline
                after the first visit, and can be pinned to the home
                screen with a real icon — just like a native app.
                But nothing is installed. Nothing goes through the App
                Store or Google Play. It just opens.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.65, color: "#4a3c2c", margin: "0 0 8px", maxWidth: 620 }}>
                That means every phone in the room — <strong>iPhone, Samsung, Google Pixel,
                Oppo, Xiaomi, OnePlus, Motorola, even a Nokia from 2019</strong> — opens the
                exact same experience. No exclusions. No "please install our app first".
              </p>
            </div>
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 12,
            }}>
              {[
                { title: "Loads in ~400ms", body: "Half the size of a single Instagram photo — even on 3G in a basement." },
                { title: "Works offline", body: "Once opened, the shell caches. Second tap is instant, no signal needed." },
                { title: "Add to home screen", body: "Pin the icon. Feels native. Still no App Store approval to wait for." },
                { title: "Auto-updates", body: "Push a new menu at 5pm — every taps after 5:00:01 sees it. No re-review." },
              ].map((c, i) => (
                <div key={i} data-testid={`pwa-benefit-${i}`} style={{
                  background: CREAM, borderRadius: 14, padding: 18,
                  border: `1px solid ${ORANGE}22`,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: ORANGE_DEEP, marginBottom: 6 }}>{c.title}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: "#4a3c2c" }}>{c.body}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* ─── Platform coverage bar ─────────────────────────── */}
      <div style={{ background: INK, color: "#fff" }}>
        <Section testid="tapnfc-platforms" style={{ paddingTop: 40, paddingBottom: 40 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 24, alignItems: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: ORANGE_SOFT }}>
              Works on every phone your customers already own:
            </div>
            {[
              { label: "iPhone", detail: "XS and newer (2018+)" },
              { label: "Samsung", detail: "Galaxy S6 and newer (2015+)" },
              { label: "Google Pixel", detail: "Every model" },
              { label: "Oppo / Xiaomi / OnePlus", detail: "Every model since 2016" },
              { label: "QR fallback", detail: "For anything older" },
            ].map((p, i) => (
              <div key={i} data-testid={`platform-badge-${i}`} style={{ borderLeft: `2px solid ${ORANGE}`, paddingLeft: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 800 }}>{p.label}</div>
                <div style={{ fontSize: 12, color: "#a8927a", marginTop: 2 }}>{p.detail}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* ─── Problem / Promise ─────────────────────────── */}
      <Section testid="tapnfc-problem" style={{ paddingTop: 60, paddingBottom: 60 }}>
        <div style={{ maxWidth: 780, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: ORANGE_DEEP, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
            The gap you're feeling
          </div>
          <H2>QR codes lost their magic. Native apps are a moat you can't cross.</H2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: "#3a2e20", margin: 0 }}>
            Your customers won't download another app. Your team can't
            hand them a link on a busy Friday night. And every QR you
            print gets covered by a menu holder, spilled coffee, or a
            competitor's sticker. HustleTap replaces all of it with a
            physical thing that already lives on every phone — the
            NFC chip — and a web experience that opens before the
            barista finishes pouring.
          </p>
        </div>
      </Section>

      {/* ─── Feature grid: PWA + iOS ─────────────────── */}
      <div style={{ background: "#fff2df" }}>
        <Section testid="tapnfc-features" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <H2>Two ways to serve. One tap.</H2>
            <P dim>The PWA runs identically on every iPhone, Samsung, Pixel, Oppo,
              Xiaomi and OnePlus made this decade. The optional iOS shell adds
              Wallet passes, Core NFC write flows and native contact saves for
              customers on Apple devices; Android already ships those features
              in the OS, so no extra app is needed there.</P>
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 20,
          }}>
            {[
              { emoji: "◉", title: "One-tap PWA", body: "Under 40 KB shell. Loads in under 300 ms on 4G. Works offline once cached. Installs to home screen without an app store — same experience on iPhone, Samsung, Pixel, Oppo, Xiaomi, OnePlus." },
              { emoji: "☰", title: "Dynamic destinations", body: "Every tag routes through your dashboard — swap a menu link for a review form, an event ticket for a coupon, without reprinting a single sticker." },
              { emoji: "❖", title: "Wallet passes", body: "Apple Wallet on iPhone via our iOS shell; Google Wallet on every Android — same .pkpass / .json source, both platforms get a native pass that updates over the air." },
              { emoji: "❤", title: "Contact & vCard save", body: "One tap creates a full contact card — logo, socials, hours, address — that saves natively into Contacts on iOS AND Android (Samsung Contacts, Google Contacts, everywhere)." },
              { emoji: "✎", title: "Review router", body: "Post-scan, guests pick Google, TripAdvisor or Instagram in a soft-gate you toggle per venue. Bad review? Route it to your inbox, not the internet." },
              { emoji: "⌘", title: "Batch & activate", body: "Order 1,000 dormant tags. Ship to franchisees. Each one activates the moment a manager taps it with the admin PIN — zero pre-provisioning." },
            ].map((f, i) => (
              <div key={i} data-testid={`feature-card-${i}`} style={{
                background: "#fff", borderRadius: 20, padding: 26,
                border: `1px solid ${ORANGE}22`,
                boxShadow: "0 8px 24px rgba(15,10,5,0.05)",
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DEEP} 100%)`,
                  color: "#fff", fontSize: 22, fontWeight: 900,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 18, boxShadow: "0 6px 14px rgba(255,106,19,0.35)",
                }}>{f.emoji}</div>
                <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 8, color: INK }}>{f.title}</div>
                <div style={{ fontSize: 15, lineHeight: 1.55, color: "#4a3c2c" }}>{f.body}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* ─── How it works ─────────────────────────────── */}
      <Section testid="tapnfc-how" style={{ paddingTop: 96, paddingBottom: 96 }}>
        <div id="how" style={{ position: "absolute", marginTop: -100 }} />
        <div style={{ maxWidth: 780, marginBottom: 56, textAlign: "center", marginLeft: "auto", marginRight: "auto" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: ORANGE_DEEP, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
            How it works
          </div>
          <H2>Three steps from unboxing to first tap.</H2>
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 24,
        }}>
          {[
            { n: "01", title: "Order tags", body: "Pick tabletop, wristband, sticker, keychain or standee. Each ships with a QR fallback etched next to the NFC chip, so scanners without NFC still get in.", img: IMG.wallet },
            { n: "02", title: "Point them", body: "In the dashboard, drag any destination — menu, review, booking, coupon, contact card, video — onto any tag. Change your mind twice a day. Every tap always follows the latest setting.", img: IMG.device },
            { n: "03", title: "Watch the data", body: "See every tap live: which tag, which hour, which device, which language. Route weak reviews to your inbox, hot leads to your CRM, tips to Stripe.", img: IMG.hands },
          ].map((s, i) => (
            <div key={i} data-testid={`how-step-${i}`} style={{
              background: "#fff", borderRadius: 22, overflow: "hidden",
              border: `1px solid ${ORANGE}22`,
              boxShadow: "0 12px 30px rgba(15,10,5,0.06)",
            }}>
              <img src={s.img} alt={`Step ${s.n}: ${s.title}`}
                   style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }} />
              <div style={{ padding: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: ORANGE_DEEP, letterSpacing: "0.1em", marginBottom: 8 }}>{s.n}</div>
                <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 10 }}>{s.title}</div>
                <div style={{ fontSize: 15, lineHeight: 1.55, color: "#4a3c2c" }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── Use cases ─────────────────────────────────── */}
      <div style={{ background: `linear-gradient(180deg, ${CREAM} 0%, #fff 100%)` }}>
        <Section testid="tapnfc-uses" style={{ paddingTop: 96, paddingBottom: 96 }}>
          <div id="uses" style={{ position: "absolute", marginTop: -100 }} />
          <div style={{ marginBottom: 56, textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: ORANGE_DEEP, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
              Real deployments
            </div>
            <H2>Built for the businesses that live and die on a Friday-night rush.</H2>
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 20,
          }}>
            {[
              { img: IMG.cafe, title: "Hospitality", body: "A tabletop tag becomes menu → order → review router. Fewer waiter-flags, higher tip conversion, and every unhappy patron is caught before they hit Google." },
              { img: IMG.event, title: "Events & venues", body: "Wristbands unlock check-in, cashless drinks, and a post-event replay page — the same tag your bouncer scanned at the door." },
              { img: IMG.clinic, title: "Clinics & health", body: "Waiting-room stickers open patient forms, HICAPS receipts and appointment rebooking without a single business card." },
              { img: IMG.tap, title: "Retail & wholesale", body: "Shelf-edge tags tell the story behind a bottle of wine, then drop a discount code straight into Apple Wallet the moment a shopper taps it." },
            ].map((u, i) => (
              <div key={i} data-testid={`use-case-${i}`} style={{
                background: "#fff", borderRadius: 22, overflow: "hidden",
                border: `1px solid ${ORANGE}22`,
                transition: "transform .2s ease",
              }}>
                <img src={u.img} alt={u.title} style={{ width: "100%", height: 210, objectFit: "cover", display: "block" }} />
                <div style={{ padding: 22 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10, color: INK }}>{u.title}</div>
                  <div style={{ fontSize: 14, lineHeight: 1.55, color: "#4a3c2c" }}>{u.body}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* ─── Compare ─────────────────────────────────── */}
      <Section testid="tapnfc-compare" style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div style={{
          background: `linear-gradient(135deg, ${INK} 0%, #2a1c0e 100%)`,
          color: "#fff", padding: "48px 44px", borderRadius: 28,
          boxShadow: "0 30px 60px rgba(255,106,19,0.15)",
        }}>
          <div style={{ maxWidth: 720, marginBottom: 32 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: ORANGE_SOFT, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>
              Why tap beats scan
            </div>
            <h2 style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: 1.15, margin: 0, letterSpacing: "-0.02em", fontWeight: 800 }}>
              HustleTap vs QR-only signage.
            </h2>
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "1.2fr repeat(2, 1fr)",
            gap: 0, background: "#1a1108", borderRadius: 14, overflow: "hidden",
          }}>
            {[
              ["", "HustleTap", "QR sticker"],
              ["Time to first pixel", "0.4s (tap)", "3.2s (align + focus)"],
              ["Rain / grease / scratch", "Works", "Half-scans"],
              ["Dark room / low light", "Works", "Camera dies"],
              ["Change destination", "One dashboard click", "Reprint + restick"],
              ["Add to Wallet natively", "Yes (iOS shell)", "No"],
              ["Read tap-by-tap analytics", "Real-time", "Whatever your redirect logs"],
              ["Works without an app", "Yes (PWA)", "Yes"],
            ].map((row, r) => (
              <React.Fragment key={r}>
                {row.map((cell, c) => (
                  <div key={c} style={{
                    padding: "14px 18px", fontSize: 14,
                    borderBottom: r < 7 ? "1px solid #2a1c0e" : "none",
                    background: r === 0 ? "#2a1c0e" : "transparent",
                    fontWeight: r === 0 ? 800 : (c === 0 ? 600 : 400),
                    color: r === 0 ? ORANGE_SOFT : (c === 1 && r > 0 ? "#fff" : (c === 2 && r > 0 ? "#a8927a" : "#fff")),
                  }}>{cell}</div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Section>

      {/* ─── Pricing tease ───────────────────────────── */}
      <div id="pricing" style={{ position: "absolute", marginTop: -100 }} />
      <Section testid="tapnfc-pricing" style={{ paddingTop: 40, paddingBottom: 80 }}>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 22,
        }}>
          {[
            { name: "Starter", price: "$0", per: "with 10 free tags", body: "One venue · unlimited taps · Google review router · Apple Wallet loyalty pass · dashboard analytics.", cta: "Claim tags", featured: false },
            { name: "Growth", price: "$29", per: "per venue / month", body: "Everything in Starter · unlimited venues · CRM webhooks · Zapier · custom fallback URL · white-label subdomain.", cta: "Start 14-day trial", featured: true },
            { name: "Enterprise", price: "Let's talk", per: "1,000+ tags", body: "Custom tag artwork · SSO · SLAs · on-premise dashboard · dedicated success manager · procurement-friendly billing.", cta: "Book a call", featured: false },
          ].map((p, i) => (
            <div key={i} data-testid={`pricing-card-${i}`} style={{
              background: p.featured ? `linear-gradient(180deg, ${ORANGE} 0%, ${ORANGE_DEEP} 100%)` : "#fff",
              color: p.featured ? "#fff" : INK,
              borderRadius: 24, padding: 32,
              border: p.featured ? "none" : `1px solid ${ORANGE}22`,
              boxShadow: p.featured
                ? "0 24px 50px rgba(255,106,19,0.35)"
                : "0 8px 24px rgba(15,10,5,0.05)",
              transform: p.featured ? "translateY(-8px)" : "none",
              position: "relative",
            }}>
              {p.featured && (
                <div style={{
                  position: "absolute", top: -12, left: 24,
                  background: INK, color: ORANGE_SOFT,
                  padding: "4px 12px", borderRadius: 999,
                  fontSize: 11, fontWeight: 900, letterSpacing: "0.08em",
                }}>MOST POPULAR</div>
              )}
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6, letterSpacing: "0.05em", textTransform: "uppercase" }}>{p.name}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: "-0.02em" }}>{p.price}</div>
                <div style={{ fontSize: 13, opacity: 0.75 }}>{p.per}</div>
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 24, opacity: p.featured ? 0.95 : 0.85 }}>{p.body}</div>
              <CTA href="#try" testid={`pricing-cta-${i}`} secondary={!p.featured} onClick={openOrder}>{p.cta}</CTA>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── FAQ ─────────────────────────────────────── */}
      <div style={{ background: "#fff2df" }}>
        <Section testid="tapnfc-faq" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <H2>Questions we get most weeks.</H2>
          </div>
          <div style={{ maxWidth: 820, margin: "0 auto" }}>
            {[
              { q: "Do my customers need an app?", a: "No. The PWA opens in Safari or Chrome on the very first tap — nothing to install. This is true whether your customer walks in with an iPhone, a Samsung Galaxy, a Pixel, an Oppo, a Xiaomi, or a OnePlus. The optional iOS shell only exists for us as the venue owner if we want Apple Wallet pass minting and native Core NFC write flows." },
              { q: "Will old iPhones and old Samsungs tap in?", a: "Yes. Background NFC has shipped on every iPhone since XS (2018), which covers ~96% of iPhones in use in Australia today. Every Samsung Galaxy since the S6 (2015), every Pixel since 2016, and every mid-range Oppo/Xiaomi since ~2018 also tap natively. Anything older still gets the QR fallback etched into every tag." },
              { q: "How do I stop competitors stickering over my tag?", a: "Every tap is signed, so a re-stickered tag is a broken tag — you'll see the reader ID stop matching the venue on your dashboard within one scan. We also ship tamper-evident housings." },
              { q: "Can I re-point 500 tags at once?", a: "One dashboard click. Every tag routes through us, so you can move a whole franchise from a Xmas promo to a Boxing-Day deal with a single toggle. Works identically for iOS and Android taps." },
              { q: "Is HustleTap GDPR / Australian Privacy Act safe?", a: "Yes. Tap data lives in the same encrypted MongoDB cluster as the rest of HustleSuite, hosted in Sydney (ap-southeast-2). Nothing leaks to third-party pixels unless you turn one on." },
              { q: "What if the NFC chip fails on an older phone?", a: "Every tag ships with a QR fallback etched into the artwork. If the chip ever misreads — or a customer walks in with a phone too old to tap — a scan does the same thing. Same URL, same routing, same experience." },
            ].map((f, i) => (
              <details key={i} data-testid={`faq-item-${i}`} style={{
                background: "#fff", borderRadius: 14, padding: "18px 22px",
                marginBottom: 12, border: `1px solid ${ORANGE}22`,
              }}>
                <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: 16, listStyle: "none", color: INK, position: "relative", paddingRight: 28 }}>
                  {f.q}
                  <span style={{ position: "absolute", right: 0, top: 2, color: ORANGE, fontSize: 20 }}>+</span>
                </summary>
                <p style={{ margin: "12px 0 0", color: "#4a3c2c", fontSize: 15, lineHeight: 1.6 }}>{f.a}</p>
              </details>
            ))}
          </div>
        </Section>
      </div>

      {/* ─── Final CTA ─────────────────────────────── */}
      <div id="try" style={{ position: "absolute", marginTop: -100 }} />
      <div style={{
        background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DEEP} 100%)`,
        color: "#fff",
      }}>
        <Section testid="tapnfc-final-cta" style={{ paddingTop: 96, paddingBottom: 96, textAlign: "center" }}>
          <h2 style={{
            fontSize: "clamp(32px, 4.6vw, 56px)", lineHeight: 1.05,
            margin: "0 0 20px", letterSpacing: "-0.03em", fontWeight: 900,
          }}>Order your first ten tags. Free.</h2>
          <p style={{
            fontSize: 19, lineHeight: 1.5, margin: "0 auto 36px",
            maxWidth: 640, opacity: 0.95,
          }}>
            Ships from Brisbane in 48 hours. Every tag comes activated,
            printed with your logo, and locked to your venue. If they don't
            lift your review count in 30 days, we buy them back.
          </p>
          <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <a href="#try" data-testid="final-cta-order" onClick={openOrder}
               style={{
                 padding: "18px 34px", borderRadius: 999,
                 fontWeight: 800, fontSize: 16, textDecoration: "none",
                 background: "#fff", color: ORANGE_DEEP,
                 boxShadow: "0 14px 30px rgba(0,0,0,0.2)", cursor: "pointer",
               }}>Ship me 10 tags →</a>
            <Link to="/login" data-testid="final-cta-login"
                  style={{
                    padding: "18px 34px", borderRadius: 999,
                    fontWeight: 800, fontSize: 16, textDecoration: "none",
                    background: "transparent", color: "#fff",
                    border: "2px solid rgba(255,255,255,0.5)",
                  }}>Sign in to dashboard</Link>
          </div>
        </Section>
      </div>

      {/* ─── Footer ────────────────────────────────── */}
      <footer style={{ background: INK, color: "#a8927a", padding: "36px 24px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 20, fontSize: 13 }} data-testid="tapnfc-footer">
          <div>© 2026 HustleTap — a HustleSuite property. Made in Brisbane.</div>
          <div style={{ display: "flex", gap: 20 }}>
            <a href="/privacy" style={{ color: "#a8927a", textDecoration: "none" }}>Privacy</a>
            <a href="/terms" style={{ color: "#a8927a", textDecoration: "none" }}>Terms</a>
            <a href="mailto:hello@hustletap.au" style={{ color: "#a8927a", textDecoration: "none" }}>hello@hustletap.au</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
